import { useEffect } from "react";
import { DatePicker, Form, Input, InputNumber, Modal, Select, Switch } from "@arco-design/web-react";

import type { MySqlQueryRecordFormValues, SelectOption } from "../types";

type MySqlQueryRecordModalProps = {
  visible: boolean;
  mode: "create" | "edit";
  compact?: boolean;
  submitting: boolean;
  initialValues: MySqlQueryRecordFormValues;
  sourceOptions: SelectOption[];
  tagOptions: SelectOption[];
  onCancel: () => void;
  onSubmit: (values: MySqlQueryRecordFormValues) => Promise<void>;
};

const defaultValidationTrigger = ["onBlur", "onChange"];

/**
 * MySQL 查询记录新增/编辑弹窗。
 */
export function MySqlQueryRecordModal({
  visible,
  mode,
  compact = false,
  submitting,
  initialValues,
  sourceOptions,
  tagOptions,
  onCancel,
  onSubmit,
}: MySqlQueryRecordModalProps) {
  const [form] = Form.useForm<MySqlQueryRecordFormValues>();

  useEffect(() => {
    if (!visible) {
      return;
    }
    form.setFieldsValue(initialValues);
  }, [form, initialValues, visible]);

  return (
    <Modal
      visible={visible}
      title={mode === "create" ? "新增记录" : "编辑记录"}
      className={compact ? "mysql-query-record-modal is-compact" : "mysql-query-record-modal"}
      okText={mode === "create" ? "创建" : "保存"}
      cancelText="取消"
      confirmLoading={submitting}
      style={{
        width: compact ? "calc(100vw - 24px)" : 560,
        maxWidth: "100%",
      }}
      wrapStyle={compact ? { padding: "12px 0" } : undefined}
      alignCenter={!compact}
      onCancel={onCancel}
      onOk={() => handleSubmit()}
      maskClosable={false}
      escToExit={false}
      closable={false}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          field="recordName"
          label="记录名称"
          validateTrigger={defaultValidationTrigger}
          rules={[
            { required: true, message: "请输入记录名称" },
            {
              validator: (value, callback) => {
                if (!String(value ?? "").trim()) {
                  callback("请输入记录名称");
                  return;
                }
                callback();
              },
            },
            { maxLength: 128, message: "记录名称不能超过 128 个字符" },
          ]}
        >
          <Input allowClear placeholder="请输入记录名称" />
        </Form.Item>

        <Form.Item
          field="source"
          label="数据来源"
          validateTrigger={defaultValidationTrigger}
          rules={[{ required: true, message: "请选择数据来源" }]}
        >
          <Select placeholder="请选择数据来源" options={sourceOptions} />
        </Form.Item>

        <Form.Item field="tags" label="标签">
          <Select mode="multiple" allowClear placeholder="请选择标签" options={tagOptions} />
        </Form.Item>

        <Form.Item
          field="bizDate"
          label="业务日期"
          validateTrigger={defaultValidationTrigger}
          rules={[{ required: true, message: "请选择业务日期" }]}
        >
          <DatePicker placeholder="请选择业务日期" />
        </Form.Item>

        <Form.Item
          field="amount"
          label="金额"
          validateTrigger={defaultValidationTrigger}
          rules={[
            { required: true, message: "请输入金额" },
            {
              validator: (value, callback) => {
                if (typeof value !== "number" || Number.isNaN(value)) {
                  callback("请输入金额");
                  return;
                }
                if (value < 0) {
                  callback("金额不能小于 0");
                  return;
                }
                callback();
              },
            },
          ]}
        >
          <InputNumber precision={2} min={0} placeholder="请输入金额" />
        </Form.Item>

        <Form.Item field="enabled" label="是否启用" triggerPropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );

  /** 校验表单并向页面层提交规范化后的 CRUD 参数。 */
  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validate();
      await onSubmit({
        ...values,
        recordName: values.recordName.trim(),
        tags: values.tags ?? [],
        enabled: values.enabled ?? true,
      });
    } catch {
      // 表单校验失败时保持弹窗打开，由字段级提示引导用户修正。
    }
  }
}
